import { useEffect, useState } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Switch,
  Tag,
  message,
  Popconfirm,
  Row,
  Col,
  Statistic,
  Tabs,
  Card,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SendOutlined,
  GiftOutlined,
  DollarOutlined,
  PercentageOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { couponAPI, memberAPI } from '../../api'
import { confirmDelete, confirmStatusChange } from '../../hooks/useConfirm'

const { Option } = Select
const { RangePicker } = DatePicker
const { TextArea } = Input

// 与后端契约一致的优惠券字段
interface Coupon {
  id: number
  name: string
  type: string            // 'fixed' | 'percent' | 'buyXgetY'
  value: number           // fixed: 满减额；percent: 每100元减value元
  conditionAmount: number // 满额门槛，0 或 null 表示无门槛
  applyScope: string
  applyIds: string | null
  startDate: string       // ISO 字符串
  endDate: string         // ISO 字符串
  status: string          // 'active' | 'inactive'
  description?: string
  createdAt?: string
  _count?: { userCoupons: number } // 领取数
}

// 列表「类型」列映射（前端展示用新类型值）
const typeMap: Record<string, { text: string; color: string; icon: any }> = {
  fixed: { text: '满减券', color: 'green', icon: <DollarOutlined /> },
  percent: { text: '折扣券', color: 'blue', icon: <PercentageOutlined /> },
  buyXgetY: { text: '买赠券', color: 'orange', icon: <GiftOutlined /> },
}

// 编辑回显时：后端新类型 → 表单老类型（后端兼容老格式，表单仍提交老值）
const mapTypeToForm: Record<string, string> = {
  fixed: 'reduce',
  percent: 'discount',
  buyXgetY: 'buyXgetY',
}

const Coupons = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [grantVisible, setGrantVisible] = useState(false)
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null)
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null)
  const [activeTab, setActiveTab] = useState('all')
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [form] = Form.useForm()
  const [grantForm] = Form.useForm()

  const [stats, setStats] = useState({
    totalCoupons: 0,
    activeCoupons: 0,
    totalReceived: 0,
    totalUsed: 0,
  })

  // 真实用户名单（打开发放弹窗时从后端拉取）
  const [users, setUsers] = useState<Array<{ id: number; label: string; phone: string }>>([])
  const [usersLoading, setUsersLoading] = useState(false)

  useEffect(() => {
    fetchCoupons()
  }, [activeTab, pagination.current, pagination.pageSize])

  const fetchCoupons = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: pagination.current,
        pageSize: pagination.pageSize,
      }
      if (activeTab === 'active') params.status = 'active'
      if (activeTab === 'inactive') params.status = 'inactive'

      const res = await couponAPI.getList(params)
      if (res.code === 200) {
        const list = res.data.list || []
        setCoupons(list)
        setPagination((prev) => ({ ...prev, total: res.data.total || 0 }))
        if (res.data.stats) {
          setStats(res.data.stats)
        } else {
          setStats({
            totalCoupons: list.length,
            activeCoupons: list.filter((c: Coupon) => c.status === 'active').length,
            totalReceived: list.reduce(
              (sum: number, c: Coupon) => sum + (c._count?.userCoupons || 0),
              0
            ),
            totalUsed: 0, // 使用数后端暂无统计
          })
        }
      }
    } catch (error) {
      message.error('获取优惠券失败')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingCoupon(null)
    form.resetFields()
    form.setFieldsValue({
      status: true,
      type: 'reduce',
      discountType: 'amount',
      totalQuantity: 1000,
      conditionAmount: 0,
      conditionQuantity: 0,
      applyScope: 'all',
    })
    setModalVisible(true)
  }

  const handleEdit = (record: Coupon) => {
    setEditingCoupon(record)
    // 后端新字段 → 表单老字段映射
    form.setFieldsValue({
      name: record.name,
      type: mapTypeToForm[record.type] || record.type,
      discountValue: record.value,
      conditionAmount: record.conditionAmount || 0,
      totalQuantity: 1000,
      conditionQuantity: 0,
      applyScope: record.applyScope || 'all',
      description: record.description || '',
      status: record.status === 'active',
      dateRange: [dayjs(record.startDate), dayjs(record.endDate)],
    })
    setModalVisible(true)
  }

  const handleDelete = (id: number, name: string) => {
    confirmDelete(name, '优惠券管理', () =>
      couponAPI.delete(id).then(() => {
        message.success('删除成功')
        fetchCoupons()
      })
    )
  }

  // 状态开关：后端只有 PUT，没有 PATCH —— 走 PUT /api/coupons/:id
  const handleToggleStatus = (id: number, status: boolean, name: string) => {
    confirmStatusChange(name, status, '优惠券管理', () =>
      couponAPI
        .update(id, { status: status ? 'active' : 'inactive' })
        .then((res: any) => {
          if (res.code === 200) {
            message.success(status ? '已启用' : '已停用')
            fetchCoupons()
          }
        })
    )
  }

  // 打开发放弹窗时拉取真实用户名单
  const handleGrant = async (record: Coupon) => {
    setSelectedCoupon(record)
    grantForm.resetFields()
    grantForm.setFieldsValue({ quantity: 1 })
    setGrantVisible(true)
    try {
      setUsersLoading(true)
      const res = await memberAPI.getList({ page: 1, pageSize: 1000 })
      setUsersLoading(false)
      if (res.code === 200) {
        const list = res.data.list || []
        setUsers(
          list.map((u: any) => ({
            id: u.id,
            label: `${u.shopName || ''}(${u.contactName || ''})`,
            phone: u.phone || '',
          }))
        )
      } else {
        setUsers([])
      }
    } catch (error) {
      setUsersLoading(false)
      message.error('获取用户列表失败')
      setUsers([])
    }
  }

  const handleGrantSubmit = async () => {
    if (!selectedCoupon) return
    try {
      const values = await grantForm.validateFields()
      await couponAPI.grant(selectedCoupon.id, {
        userIds: values.userIds,
        quantity: values.quantity || 1, // 后端忽略 quantity，每用户固定发 1 张
      })
      message.success(`成功发放给 ${values.userIds.length} 位用户`)
      setGrantVisible(false)
      fetchCoupons()
    } catch (error) {
      message.error('发放失败')
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const data = {
        ...values,
        startDate: values.dateRange[0].format('YYYY-MM-DD'),
        endDate: values.dateRange[1].format('YYYY-MM-DD'),
        dateRange: undefined,
      }

      if (editingCoupon) {
        const res = await couponAPI.update(editingCoupon.id, data)
        if (res.code === 200) {
          message.success('更新成功')
        }
      } else {
        const res = await couponAPI.create(data)
        if (res.code === 200) {
          message.success('创建成功')
        }
      }
      setModalVisible(false)
      // 成功后重新拉取列表，避免本地拼数据导致的格式不一致
      fetchCoupons()
    } catch (error) {
      message.error('操作失败')
    }
  }

  // ISO 字符串取前 10 位（YYYY-MM-DD）
  const fmtDate = (d?: string) => (d ? d.slice(0, 10) : '')

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: '优惠券名称', dataIndex: 'name', key: 'name', width: 150 },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (val: string) => (
        <Tag color={typeMap[val]?.color} icon={typeMap[val]?.icon}>
          {typeMap[val]?.text || val}
        </Tag>
      ),
    },
    {
      title: '优惠内容',
      key: 'content',
      width: 150,
      render: (_: any, record: Coupon) => {
        if (record.type === 'fixed') {
          return record.conditionAmount > 0
            ? `满${record.conditionAmount}减${record.value}元`
            : `减${record.value}元`
        }
        if (record.type === 'percent') return `每100元减${record.value}元`
        if (record.type === 'buyXgetY') return '买赠券'
        return '-'
      },
    },
    {
      title: '领取/使用',
      key: 'usage',
      width: 120,
      render: (_: any, record: Coupon) => (
        <span>{record._count?.userCoupons || 0} / -</span>
      ),
    },
    {
      title: '有效期',
      key: 'validity',
      width: 180,
      render: (_: any, record: Coupon) => (
        <span>
          {fmtDate(record.startDate)} ~ {fmtDate(record.endDate)}
        </span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (val: string, record: Coupon) => (
        <Switch
          checked={val === 'active'}
          size="small"
          onChange={(checked) => handleToggleStatus(record.id, checked, record.name)}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right' as const,
      render: (_: any, record: Coupon) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            icon={<SendOutlined />}
            onClick={() => handleGrant(record)}
          >
            发放
          </Button>
          <Popconfirm
            title="确定删除吗？"
            onConfirm={() => handleDelete(record.id, record.name)}
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="coupons-page">
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="优惠券总数"
              value={stats.totalCoupons}
              prefix={<GiftOutlined />}
              valueStyle={{ color: '#006835' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="进行中"
              value={stats.activeCoupons}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="累计领取"
              value={stats.totalReceived}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="累计使用"
              value={stats.totalUsed}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small">
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="全部" key="all" />
          <Tabs.TabPane tab="进行中" key="active" />
          <Tabs.TabPane tab="已结束" key="inactive" />
        </Tabs>

        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            创建优惠券
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={coupons}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) =>
              setPagination({ ...pagination, current: page, pageSize }),
          }}
        />
      </Card>

      {/* 创建/编辑优惠券弹窗 */}
      <Modal
        title={editingCoupon ? '编辑优惠券' : '创建优惠券'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="优惠券名称"
            rules={[
              { required: true, message: '请输入名称' },
              { max: 50, message: '名称最多50个字符' },
            ]}
          >
            <Input placeholder="请输入优惠券名称" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="优惠券类型"
                rules={[{ required: true, message: '请选择类型' }]}
              >
                <Select placeholder="请选择类型">
                  <Option value="discount">折扣券</Option>
                  <Option value="reduce">满减券</Option>
                  <Option value="buyXgetY">买赠券</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="discountValue"
                label="优惠值"
                rules={[
                  { required: true, message: '请输入优惠值' },
                  { type: 'number', min: 0, message: '优惠值不能为负数' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.1}
                  placeholder="折扣/金额/赠品数量"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="conditionAmount" label="使用门槛（金额）">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1}
                  placeholder="0表示无门槛"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="conditionQuantity" label="使用门槛（数量）">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={1}
                  placeholder="0表示无门槛"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="totalQuantity"
                label="发放总量"
                rules={[
                  { required: true, message: '请输入发放总量' },
                  { type: 'number', min: 1, message: '发放总量至少1张' },
                ]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1}
                  step={1}
                  placeholder="默认1000"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="applyScope" label="适用商品" initialValue="all">
                <Select>
                  <Option value="all">全部商品</Option>
                  <Option value="category">指定分类</Option>
                  <Option value="product">指定商品</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="dateRange"
            label="有效期"
            rules={[{ required: true, message: '请选择有效期' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="description" label="使用说明">
            <TextArea rows={3} placeholder="请输入使用说明" maxLength={200} showCount />
          </Form.Item>

          <Form.Item name="status" label="状态" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 发放优惠券弹窗 */}
      <Modal
        title={`发放优惠券：${selectedCoupon?.name}`}
        open={grantVisible}
        onOk={handleGrantSubmit}
        onCancel={() => setGrantVisible(false)}
        width={500}
        destroyOnClose
      >
        <Form form={grantForm} layout="vertical">
          <Form.Item
            name="userIds"
            label="选择用户"
            rules={[{ required: true, message: '请选择用户' }]}
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children
                  ? String(option.children)
                  : ''
                )
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              loading={usersLoading}
              placeholder="搜索并选择要发放的用户（店名/联系人/手机号）"
              style={{ width: '100%' }}
            >
              {users.map((u) => (
                <Option key={u.id} value={u.id}>
                  {u.label} {u.phone}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="quantity"
            label="每人发放数量"
            initialValue={1}
            rules={[
              { required: true, message: '请输入发放数量' },
              { type: 'number', min: 1, max: 10, message: '发放数量1-10张' },
            ]}
          >
            <InputNumber style={{ width: '100%' }} min={1} max={10} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Coupons